import {
  Package, ShoppingCart, Settings, LayoutDashboard,
  ShoppingBag, Banknote, Receipt, ClipboardList, FolderOpen, Zap,
} from '@/components/icons';
import type { ModuleKey } from '@erp/data-access/interfaces';

export interface NavItem {
  labelKey: string;
  path: string;
  icon?: React.ReactNode;
  addPath?: string;
  children?: NavItem[];
  adminOnly?: boolean;
  /** If true, clicking the parent label navigates to `path` and the chevron
   * (a separate target) toggles the collapsible. Use for parents whose path
   * resolves to a real route (e.g. Caisse → /caisse = POS screen). */
  navigable?: boolean;
  /** Rendered as a small rounded pill on the right side of the row. */
  badge?: number;
  /** If set, the item is hidden when the matching module is disabled in
   * `module_settings`. Items with no module key are always visible. */
  module?: ModuleKey;
}

export interface NavGroup {
  labelKey?: string;
  items: NavItem[];
  /** If set, the entire group is hidden when the matching module is
   * disabled. Use to collapse a whole section in one go (e.g. "Achats"). */
  module?: ModuleKey;
}

const I = 16;

export const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.groups.main',
    items: [
      { labelKey: 'nav.dashboard', path: '/', icon: <LayoutDashboard size={I} /> },
    ],
  },
  {
    labelKey: 'nav.groups.operations',
    items: [
      {
        labelKey: 'nav.caisse', path: '/caisse', icon: <Receipt size={I} />,
        navigable: true,
        module: 'caisse',
        children: [
          { labelKey: 'nav.posScreen', path: '/caisse/pos' },
          { labelKey: 'nav.caisseRegisters', path: '/caisse/registers' },
          { labelKey: 'nav.caisseSessions', path: '/caisse/sessions' },
        ],
      },
      {
        labelKey: 'nav.sales', path: '/sales', icon: <ShoppingCart size={I} />,
        module: 'sales',
        children: [
          { labelKey: 'nav.quotes', path: '/sales/quotes', addPath: '/sales/quotes/new' },
          { labelKey: 'nav.orders', path: '/sales/orders', addPath: '/sales/orders/new' },
          { labelKey: 'nav.deliveries', path: '/sales/deliveries', addPath: '/sales/deliveries/new' },
          { labelKey: 'nav.invoices', path: '/sales/invoices', addPath: '/sales/invoices/new' },
          { labelKey: 'nav.creditNotes', path: '/sales/credit-notes', addPath: '/sales/credit-notes/new' },
          { labelKey: 'nav.clients', path: '/clients' },
          { labelKey: 'nav.representatives', path: '/representatives' },
        ],
      },
      {
        labelKey: 'nav.purchases', path: '/purchases', icon: <ShoppingBag size={I} />,
        module: 'purchases',
        children: [
          { labelKey: 'nav.suppliers', path: '/suppliers' },
          { labelKey: 'nav.purchaseOrders', path: '/purchases/orders', addPath: '/purchases/orders/new' },
          { labelKey: 'nav.receptions', path: '/purchases/receptions', addPath: '/purchases/receptions/new' },
          { labelKey: 'nav.purchaseInvoices', path: '/purchases/invoices', addPath: '/purchases/invoices/new' },
          { labelKey: 'nav.purchaseCreditNotes', path: '/purchases/credit-notes', addPath: '/purchases/credit-notes/new' },
        ],
      },
      {
        labelKey: 'nav.articles', path: '/articles', icon: <Package size={I} />,
        navigable: true,
        children: [
          { labelKey: 'nav.articlesList', path: '/articles' },
          { labelKey: 'articles.categories.title', path: '/articles/categories' },
          { labelKey: 'articles.families', path: '/articles/families' },
          { labelKey: 'articles.brands', path: '/articles/brands' },
          { labelKey: 'nav.stockMovements', path: '/stock', module: 'stock' },
          { labelKey: 'nav.stockExits', path: '/stock/vehicle-exits', addPath: '/stock/vehicle-exits/new', module: 'stock' },
          { labelKey: 'nav.stockEntries', path: '/stock/vehicle-entries', addPath: '/stock/vehicle-entries/new', module: 'stock' },
          { labelKey: 'nav.warehouses', path: '/stock/warehouses', module: 'stock' },
          { labelKey: 'nav.inventories', path: '/stock/inventories', module: 'stock' },
        ],
      },
      {
        labelKey: 'nav.groups.finance', path: '/payments', icon: <Banknote size={I} />,
        children: [
          { labelKey: 'nav.paymentsAll', path: '/payments', module: 'payments' },
          { labelKey: 'nav.paymentsRecurring', path: '/payments/recurring', module: 'payments' },
          { labelKey: 'nav.treasuryNav', path: '/treasury', module: 'treasury' },
          { labelKey: 'nav.checksOnly', path: '/checks', module: 'checks' },
          { labelKey: 'nav.traites', path: '/traites', module: 'checks' },
          { labelKey: 'nav.withholdingSales', path: '/withholding/sales', module: 'withholding' },
          { labelKey: 'nav.withholdingPurchases', path: '/withholding/purchases', module: 'withholding' },
        ],
      },
    ],
  },
  {
    labelKey: 'nav.groups.channels',
    items: [
      {
        labelKey: 'nav.storefront', path: '/storefront', icon: <ShoppingBag size={I} />,
        navigable: true,
        module: 'storefront',
        children: [
          { labelKey: 'nav.storefrontOrders', path: '/storefront/orders' },
          { labelKey: 'nav.storefrontCustomers', path: '/storefront/customers' },
          { labelKey: 'nav.storefrontProducts', path: '/storefront/products' },
          { labelKey: 'nav.storefrontCategories', path: '/storefront/categories' },
          { labelKey: 'nav.storefrontTheme', path: '/storefront/theme' },
          { labelKey: 'nav.storefrontPages', path: '/storefront/pages' },
          { labelKey: 'nav.abandonedCarts', path: '/storefront/abandoned-carts' },
          { labelKey: 'nav.notifications', path: '/storefront/notifications' },
          { labelKey: 'nav.storeSettings', path: '/storefront/settings' },
          { labelKey: 'nav.storeSoundSettings', path: '/storefront/settings/notification-sounds' },
          { labelKey: 'nav.storefrontAnalytics', path: '/storefront/analytics' },
          { labelKey: 'nav.codCalculator', path: '/storefront/cod' },
        ],
      },
      {
        labelKey: 'nav.pro', path: '/pro', icon: <Zap size={I} />,
        navigable: true,
        module: 'pro',
        children: [
          { labelKey: 'nav.proCustomers', path: '/pro/customers' },
          { labelKey: 'nav.proProducts', path: '/pro/products' },
          { labelKey: 'nav.proCategories', path: '/pro/categories' },
          { labelKey: 'nav.proCommandes', path: '/pro/commandes' },
          { labelKey: 'nav.proWallets', path: '/pro/wallets' },
          { labelKey: 'nav.proCommissions', path: '/pro/commissions' },
          { labelKey: 'nav.proSettings', path: '/pro/settings' },
        ],
      },
    ],
  },
  {
    labelKey: 'nav.groups.settings',
    items: [
      { labelKey: 'nav.settings', path: '/settings', icon: <Settings size={I} /> },
      { labelKey: 'nav.formations', path: '/formations', icon: <ClipboardList size={I} /> },
      { labelKey: 'nav.resources', path: '/resources', icon: <FolderOpen size={I} /> },
    ],
  },
];
